import React, { useEffect, useState, useCallback, useRef } from 'react';
import { profileService } from '@services/profile.service.ts';
import { ProfileStructure, ProfileData, ProfileField, UpdateProfileDto, ProfileDataValue } from '@/types/profile.types';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  SelectChangeEvent,

  Button,
} from '@mui/material';

import { AvatarUploader } from '@components/settings/AvatarUploader.tsx';
import { useAppDispatch } from '@store/index.ts';
import { getCurrentUser } from '@store/auth/authSlice.ts';
import { getApiBaseUrl } from '@services/api.ts';
import { toastService } from '@services/toast.service.ts';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';

const DynamicField: React.FC<{
  sectionKey: string;
  field: ProfileField;
  value: any;
  onChange: (section: string, key: string, value: any) => void;
}> = React.memo(({ sectionKey, field, value, onChange }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
    onChange(sectionKey, field.fieldKey, e.target.value);
  };

  const displayValue = (value !== null && typeof value === 'object' && value.hasOwnProperty('value'))
    ? value.value
    : value;

  if (field.fieldType === 'select') {
    return (
      <FormControl fullWidth>
        <InputLabel>{field.label}</InputLabel>
        <Select value={displayValue ?? ''} label={field.label} onChange={handleChange}>
          {field.options?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      fullWidth
      label={field.label}
      placeholder={field.placeholder}
      type={field.inputType}
      value={String(displayValue ?? '')}
      onChange={handleChange}
      InputLabelProps={{ shrink: true }}
      multiline={field.fieldType === 'text' && (field.maxLength === undefined || field.maxLength > 200)}
      rows={field.fieldType === 'text' && (field.maxLength === undefined || field.maxLength > 200) ? 3 : 1}
      inputProps={{
        min: field.min,
        max: field.max,
        step: field.step,
        maxLength: field.maxLength,
        pattern: field.pattern,
      }}
    />
  );
});

export const ProfileSettingsTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const [structure, setStructure] = useState<ProfileStructure | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const originalDataRef = useRef<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupedStructure, data] = await Promise.all([
        profileService.getProfileStructure(),
        profileService.getProfileData(),
      ]);

      if (typeof groupedStructure !== 'object' || Array.isArray(groupedStructure)) {
        console.error('Received structure data:', groupedStructure);
        throw new Error('Invalid profile structure format: expected a grouped object.');
      }

      setStructure(groupedStructure);
      setProfileData(data);
      originalDataRef.current = data;
    } catch (err: any) {
      setError(err.message || 'Не вдалося завантажити дані профілю.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDataChange = useCallback((sectionKey: string, fieldKey: string, newValue: any) => {
    setProfileData(prevData => {
      if (!prevData) return null;

      const originalFieldData = prevData[sectionKey]?.[fieldKey];

      let newFieldData;
      if (originalFieldData !== null && typeof originalFieldData === 'object' && originalFieldData.hasOwnProperty('value')) {
        newFieldData = { ...originalFieldData, value: newValue };
      } else {
        newFieldData = newValue;
      }

      return {
        ...prevData,
        [sectionKey]: {
          ...prevData[sectionKey],
          [fieldKey]: newFieldData,
        },
      };
    });
  }, []);

  const extractRawValue = (field: ProfileDataValue, fieldType: string): any => {
    let raw: any;
    if (field !== null && typeof field === 'object' && field.hasOwnProperty('value')) {
      raw = (field as any).value;
    } else if (typeof field !== 'object') {
      raw = field;
    } else {
      return undefined;
    }

    if (raw === null || raw === undefined || raw === '') return raw;
    if (fieldType === 'number' || fieldType === 'decimal') {
      const num = Number(raw);
      return isNaN(num) ? raw : num;
    }
    return raw;
  };

  const handleSave = async () => {
    if (!profileData || !structure) return;
    setIsSaving(true);

    const dataToSave: UpdateProfileDto = {};
    for (const sectionKey in profileData) {
      const sectionData = profileData[sectionKey];
      if (!sectionData || typeof sectionData !== 'object' || sectionKey === 'system') continue;

      const sectionToSave: Record<string, any> = {};
      const sectionFields = structure[sectionKey]?.fields ?? [];
      for (const fieldKey in sectionData) {
        if (fieldKey === '_meta') continue;

        const fieldInfo = sectionFields.find(f => f.fieldKey === fieldKey);
        if (!fieldInfo) continue;

        const rawValue = extractRawValue(sectionData[fieldKey], fieldInfo.fieldType);
        if (rawValue === undefined) continue;

        const originalRaw = extractRawValue(
          originalDataRef.current?.[sectionKey]?.[fieldKey] ?? null,
          fieldInfo.fieldType,
        );

        // eslint-disable-next-line eqeqeq
        if (rawValue == originalRaw) continue;

        sectionToSave[fieldKey] = rawValue;
      }
      if (Object.keys(sectionToSave).length > 0) {
        dataToSave[sectionKey] = sectionToSave;
      }
    }

    if (Object.keys(dataToSave).length === 0) {
      toastService.success('Немає змін для збереження');
      setIsSaving(false);
      return;
    }

    try {
      await profileService.updateProfileData(dataToSave);
      originalDataRef.current = profileData;
      await dispatch(getCurrentUser());
      toastService.success('Профіль успішно збережено');
    } catch (err) {
      toastService.error('Не вдалося зберегти профіль.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;}

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!structure || !profileData) {
    return <Typography>Немає даних для відображення.</Typography>;
  }

  const getFieldValue = (section: string, key: string): ProfileDataValue | undefined => {
    return profileData?.[section]?.[key];
  };

  const getFullAvatarUrl = (path: string | undefined): string | undefined => {
    if (!path) return undefined;
    if (path.startsWith('http')) return path;
    const baseUrl = getApiBaseUrl().replace('/api/v1', '');
    return `${baseUrl}/${path}`;
  };

  return (
    <Box sx={{ pb: '100px' }} data-i-doc={TabIndices.SETTINGS_PROFILE}>
      <AvatarUploader
        currentAvatarUrl={getFullAvatarUrl(profileData.general?.avatar as string | undefined)}
        onUploadSuccess={(newPath) => {
          setProfileData(prev => prev ? { ...prev, general: { ...prev.general, avatar: newPath } } : prev);
          dispatch(getCurrentUser());
          toastService.success('Аватар успішно оновлено');
        }}
      />

      {Object.entries(structure).map(([sectionKey, section]) => {
        let fields = Array.isArray(section?.fields) ? section.fields : [];

        if (sectionKey === 'general') {
          fields = fields.filter(f => f.fieldKey !== 'avatar');
        }

        if (fields.length === 0) return null;

        const sectionTitle = section.label || sectionKey;

        return (
          <Paper key={sectionKey} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>{sectionTitle}</Typography>
            <Grid container spacing={3}>
              {fields.map((fieldData) => (
                <Grid item xs={12} key={fieldData.fieldKey}>
                  <DynamicField
                    sectionKey={sectionKey}
                    field={fieldData}
                    value={getFieldValue(sectionKey, fieldData.fieldKey)}
                    onChange={handleDataChange}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        );
      })}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={isSaving} size="large">
          {isSaving ? <CircularProgress size={24} /> : 'Зберегти налаштування'}
        </Button>
      </Box>
    </Box>
  );
};
